import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, useTheme, useMediaQuery } from '@mui/material';
import { Clock, Users, BarChart3, TrendingUp } from 'lucide-react';
import { AnalyticsDashboard } from '../components/reports/AnalyticsDashboard';
import { ReportsList } from '../components/reports/ReportsList';
import { ReportDialog } from '../components/reports/ReportDialog';
import { Report, ReportFilters } from '../components/reports/types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

const reports: Report[] = [
  {
    id: 'time-in-stage',
    title: 'Time in Stage Report',
    description: 'Average time cases spend in each stage of the process',
    icon: <Clock size={20} />,
    filters: ['dateRange', 'practiceArea'],
  },
  {
    id: 'attorney-workload',
    title: 'Attorney Workload Report',
    description: 'Case distribution and task analysis by attorney',
    icon: <Users size={20} />,
    filters: ['attorney', 'dateRange'],
  },
  {
    id: 'case-progression',
    title: 'Case Progression Analysis',
    description: 'Stage-by-stage progression analysis with bottleneck identification',
    icon: <TrendingUp size={20} />,
    filters: ['dateRange', 'practiceArea'],
  },
  {
    id: 'task-completion',
    title: 'Task Completion Metrics',
    description: 'Task completion rates and overdue analysis by case type',
    icon: <BarChart3 size={20} />,
    filters: ['dateRange', 'practiceArea'],
  },
];

export const Reports: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reportFilters, setReportFilters] = useState<ReportFilters>({
    dateRange: 'last-30',
    practiceArea: 'all',
    attorney: 'all',
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleReportClick = (report: Report) => {
    setSelectedReport(report);
    setReportDialogOpen(true);
  };

  const handleGenerateReport = () => {
    console.log('Generating report:', selectedReport?.title, 'with filters:', reportFilters);
    setReportDialogOpen(false);
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: 4,
      }}>
        <Typography variant="h4" sx={{ flex: 1 }}>Reports & Analytics</Typography>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            '& .MuiTabs-flexContainer': {
              gap: { xs: 0, sm: 2 },
            },
          }}
        >
          <Tab 
            label="Analytics Dashboard" 
            sx={{ 
              minWidth: { xs: 0, sm: 'auto' },
              px: { xs: 2, sm: 3 },
            }}
          />
          <Tab 
            label="Reports" 
            sx={{ 
              minWidth: { xs: 0, sm: 'auto' },
              px: { xs: 2, sm: 3 },
            }}
          />
        </Tabs>
      </Box>
      
      <TabPanel value={activeTab} index={0}>
        <AnalyticsDashboard />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <ReportsList 
          reports={reports}
          onReportClick={handleReportClick}
          isMobile={isMobile}
        />
      </TabPanel>

      <ReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        selectedReport={selectedReport}
        filters={reportFilters}
        onFilterChange={(updates) => setReportFilters(prev => ({ ...prev, ...updates }))}
        onGenerate={handleGenerateReport}
      />
    </Box>
  );
};